import { Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import { useCallback, useEffect, useRef } from 'react';
import { AblyIcon } from './components/AblyIcon';
import { useAppDownloadModal } from 'shared/src/hooks';
import { ablyUserAgent } from '@banhala/utils';
import { useRoute } from '@react-navigation/native';
import cookie from 'js-cookie';
import dayjs from 'dayjs';

// ably-webview-react의 CookieKey.RedirectWebToApp 값과 동일.
// 동일 도메인에서 두 환경이 공유될 수 있도록 이름 일치 유지.
const REDIRECT_WEB_TO_APP_COOKIE = 'ably-redirect-web-to-app';

// 자동 redirect를 발사할 known route만 허용.
// 매칭되지 않는 화면(404, 임시 페이지 등)에서는 자동 redirect 미발사.
const KNOWN_REDIRECT_ROUTE_NAMES = new Set<string>([
  'GoodsDetail',
  'MarketHome',
]);

const isKnownRedirectRoute = (name: string): boolean =>
  KNOWN_REDIRECT_ROUTE_NAMES.has(name);

type Props = {
  /**
   * 자동 redirect를 비활성화 (banner 자체는 그대로 표시).
   * 페이지 진입 즉시 앱으로 유도되면 안 되는 화면(결제 등)에서 true로 전달.
   */
  disableRedirectApp?: boolean;
};

export const AppDownloadBanner: React.FC<Props> = ({
  disableRedirectApp = false,
}) => {
  const size = useScreenSize();
  // 딥링크 목적지는 모달과 같은 곳에서 계산한다(shared `appDeeplink`).
  const { show: showModal, openInApp } = useAppDownloadModal();
  const routes = useRoute();
  const hasRedirectedRef = useRef(false);

  const onPressBanner = useCallback(() => {
    if (ablyUserAgent.isMobile) {
      openInApp();
      return;
    }
    showModal();
  }, [openInApp, showModal]);

  // 모바일 웹 자동 앱 유도: 소셜 공유 등으로 유입된 모바일 웹 사용자를 앱으로 redirect.
  // - known route에서만 트리거 (404/임시 페이지 등에서는 미발사)
  // - block_landing_app=1 쿼리가 있으면 skip (앱→웹뷰로 띄울 때 무한 redirect 방지)
  // - 쿠키로 하루 1회 제한
  useEffect(() => {
    if (hasRedirectedRef.current) return;
    if (disableRedirectApp) return;
    if (!ablyUserAgent.isMobile) return;
    if (!isKnownRedirectRoute(routes.name)) return;

    const url = new URL(window.location.href);
    if (url.searchParams.has('block_landing_app')) return;

    // utm_source=seller_*는 셀러가 의도적으로 유도한 웹 유입이라 redirect 제외
    const utmSource = url.searchParams.get('utm_source') ?? '';
    if (utmSource.startsWith('seller_')) return;

    if (cookie.get(REDIRECT_WEB_TO_APP_COOKIE)) return;

    cookie.set(REDIRECT_WEB_TO_APP_COOKIE, 'true', {
      expires: dayjs().add(1, 'day').toDate(),
    });
    hasRedirectedRef.current = true;
    openInApp();
  }, [disableRedirectApp, routes.name, openInApp]);

  return (
    <Flex
      alignItems={'center'}
      justifyContent={'center'}
      backgroundColor={'white'}
    >
      <Flex.Row
        width={size.width}
        backgroundColor={'bg_pink_primary'}
        padding={{ vertical: 8, horizontal: 10 }}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        <Flex.Row gap={8} alignItems={'center'} justifyContent={'center'}>
          <AblyIcon />
          <Typography color={'content_always_white'} type={'body4'}>
            앱에서 더 많은 상품을 볼 수 있어요!
          </Typography>
        </Flex.Row>
        <Flex
          backgroundColor={'white'}
          borderRadius={20}
          alignItems={'center'}
          justifyContent={'center'}
          height={28}
          padding={{ horizontal: 8 }}
          onPress={onPressBanner}
        >
          <Typography type={'subtitle6'} color={'content_primary'}>
            앱에서 보기
          </Typography>
        </Flex>
      </Flex.Row>
    </Flex>
  );
};
